import { type CSSProperties, type ReactNode } from 'react';
import { Button } from '@helios/ui/button';
import { NavIcon, SidebarToggleButton } from '../../shell/shared';

// ─── Status variants used across the inventory module ───

export type InventoryStatusVariant =
  | 'active'
  | 'low-stock'
  | 'out-of-stock'
  | 'expired'
  | 'inactive'
  | 'draft'
  | 'ordered'
  | 'partial'
  | 'completed'
  | 'cancelled'
  | 'received'
  | 'dispensed';

const STATUS_LABEL: Record<InventoryStatusVariant, string> = {
  active: 'Active',
  'low-stock': 'Low Stock',
  'out-of-stock': 'Out of Stock',
  expired: 'Expired',
  inactive: 'Inactive',
  draft: 'Draft',
  ordered: 'Ordered',
  partial: 'Partially Received',
  completed: 'Completed',
  cancelled: 'Cancelled',
  received: 'Received',
  dispensed: 'Dispensed',
};

const STATUS_STYLE: Record<InventoryStatusVariant, { bg: string; color: string }> = {
  active: { bg: 'var(--color-success-bg)', color: 'var(--color-success-text)' },
  'low-stock': { bg: 'var(--color-warning-bg)', color: 'var(--color-warning-text)' },
  'out-of-stock': { bg: '#fee2e2', color: '#991b1b' },
  expired: { bg: '#fee2e2', color: '#991b1b' },
  inactive: { bg: 'var(--bg-surface-2)', color: 'var(--text-muted)' },
  draft: { bg: 'var(--bg-surface-2)', color: 'var(--text-muted)' },
  ordered: { bg: '#dbeafe', color: '#1e40af' },
  partial: { bg: 'var(--color-warning-bg)', color: 'var(--color-warning-text)' },
  completed: { bg: 'var(--color-success-bg)', color: 'var(--color-success-text)' },
  cancelled: { bg: '#fee2e2', color: '#991b1b' },
  received: { bg: 'var(--color-success-bg)', color: 'var(--color-success-text)' },
  dispensed: { bg: 'var(--color-success-bg)', color: 'var(--color-success-text)' },
};

export function InventoryStatusBadge({
  status,
  label,
}: {
  status: InventoryStatusVariant;
  label?: string;
}) {
  const s = STATUS_STYLE[status];
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        height: 24,
        padding: '0 var(--sp-2)',
        borderRadius: 'var(--radius-full)',
        background: s.bg,
        color: s.color,
        fontSize: 'var(--font-size-xs)',
        fontWeight: 'var(--font-weight-medium)',
        lineHeight: '24px',
        letterSpacing: '0.12px',
        whiteSpace: 'nowrap',
      }}
    >
      {label ?? STATUS_LABEL[status]}
    </span>
  );
}

// ─── Page shell that mirrors AllPatientsPage / ProvidersPage layout ───

export function InventoryPageShell({
  title,
  subtitle,
  collapsed,
  onToggleSidebar,
  primaryAction,
  children,
}: {
  title: string;
  subtitle?: string;
  collapsed: boolean;
  onToggleSidebar?: () => void;
  primaryAction?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div
        style={{
          height: 'var(--clinic-header-height)',
          padding: '0 var(--sp-6)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '1px solid var(--border)',
          flexShrink: 0,
          background: 'var(--bg-surface)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)' }}>
          {onToggleSidebar && (
            <SidebarToggleButton collapsed={collapsed} onToggle={onToggleSidebar} />
          )}
          <div>
            <h1
              style={{
                fontSize: 'var(--font-size-xl)',
                fontWeight: 'var(--font-weight-semi)',
                lineHeight: 'var(--line-height-xl)',
                letterSpacing: '-0.44px',
                color: 'var(--text)',
                margin: 0,
              }}
            >
              {title}
            </h1>
            {subtitle && (
              <p
                style={{
                  fontSize: 'var(--font-size-sm)',
                  color: 'var(--text-muted)',
                  margin: '2px 0 0',
                }}
              >
                {subtitle}
              </p>
            )}
          </div>
        </div>
        {primaryAction && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)' }}>
            {primaryAction}
          </div>
        )}
      </div>
      {children}
    </div>
  );
}

// ─── Toolbar with search input + optional filters ───

export function InventoryListToolbar({
  search,
  onSearchChange,
  searchPlaceholder,
  filters,
}: {
  search: string;
  onSearchChange: (value: string) => void;
  searchPlaceholder: string;
  filters?: ReactNode;
}) {
  return (
    <div
      style={{
        padding: 'var(--sp-4) var(--sp-6)',
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--sp-3)',
        borderBottom: '1px solid var(--border)',
        flexShrink: 0,
      }}
    >
      <div
        style={{
          flex: 1,
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <div
          style={{
            position: 'absolute',
            left: 12,
            color: 'var(--text-muted)',
            display: 'flex',
            pointerEvents: 'none',
          }}
        >
          <NavIcon name="search" />
        </div>
        <input
          type="text"
          placeholder={searchPlaceholder}
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          style={{
            width: '100%',
            height: 40,
            padding: '0 var(--sp-4) 0 40px',
            borderRadius: 'var(--radius-md)',
            border: '1px solid var(--border)',
            background: 'var(--bg-surface)',
            fontSize: 'var(--font-size-base)',
            color: 'var(--text)',
            outline: 'none',
            fontFamily: 'var(--font-family)',
          }}
        />
      </div>
      {filters && <div style={{ display: 'flex', gap: 'var(--sp-3)' }}>{filters}</div>}
    </div>
  );
}

// ─── Lightweight filter dropdown matching AllPatientsPage style ───

export function FilterDropdown<T extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: T;
  options: ReadonlyArray<{ value: T; label: string }>;
  onChange: (value: T) => void;
}) {
  return (
    <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
      <span
        style={{
          fontSize: 'var(--font-size-sm)',
          color: 'var(--text-muted)',
          marginRight: 'var(--sp-1)',
        }}
      >
        {label}:
      </span>
      <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
        <select
          value={value}
          onChange={(e) => onChange(e.target.value as T)}
          style={{
            appearance: 'none',
            background: 'transparent',
            border: 'none',
            fontSize: 'var(--font-size-sm)',
            fontWeight: 'var(--font-weight-medium)',
            color: 'var(--text)',
            cursor: 'pointer',
            paddingRight: 18,
            fontFamily: 'var(--font-family)',
          }}
        >
          {options.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        <span
          style={{
            position: 'absolute',
            right: 0,
            top: '50%',
            transform: 'translateY(-50%)',
            pointerEvents: 'none',
            color: 'var(--text-muted)',
            display: 'flex',
          }}
        >
          <NavIcon name="chevron-down" />
        </span>
      </div>
    </div>
  );
}

// ─── Generic table wrapper used by inventory submodules ───

export function InventoryTable({
  columns,
  children,
  empty,
}: {
  columns: ReadonlyArray<{ label: string; width?: string; align?: 'left' | 'right' | 'center' }>;
  children: ReactNode;
  empty?: { show: boolean; title: string; hint: string };
}) {
  const gridCols = columns.map((c) => c.width ?? '1fr').join(' ');
  return (
    <div style={{ flex: 1, overflowY: 'auto', padding: '0 var(--sp-6) var(--sp-6)' }}>
      <div
        style={{
          background: 'var(--bg-surface)',
          borderRadius: 'var(--radius-lg)',
          border: '1px solid var(--border)',
          overflow: 'hidden',
        }}
      >
        {/* Header row */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: gridCols,
            gap: 'var(--sp-3)',
            alignItems: 'center',
            padding: 'var(--sp-3) var(--sp-4)',
            borderBottom: '1px solid var(--border)',
            background: 'var(--bg-surface-2, #f8fafc)',
          }}
        >
          {columns.map((c) => (
            <span
              key={c.label}
              style={{
                fontSize: 'var(--font-size-xs)',
                fontWeight: 'var(--font-weight-semi)',
                color: 'var(--text-muted)',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                textAlign: c.align ?? 'left',
              }}
            >
              {c.label}
            </span>
          ))}
        </div>

        {empty?.show ? (
          <div
            style={{
              padding: 'var(--sp-12) var(--sp-6)',
              textAlign: 'center',
              color: 'var(--text-muted)',
            }}
          >
            <div
              style={{
                fontSize: 'var(--font-size-base)',
                fontWeight: 'var(--font-weight-medium)',
                color: 'var(--text)',
                marginBottom: 'var(--sp-1)',
              }}
            >
              {empty.title}
            </div>
            <div style={{ fontSize: 'var(--font-size-sm)' }}>{empty.hint}</div>
          </div>
        ) : (
          children
        )}
      </div>
    </div>
  );
}

// ─── Body row helper that lines up with the table grid ───

export function InventoryRow({
  columns,
  children,
  onClick,
  clickable = false,
}: {
  columns: ReadonlyArray<{ width?: string }>;
  children: ReactNode;
  onClick?: () => void;
  clickable?: boolean;
}) {
  const gridCols = columns.map((c) => c.width ?? '1fr').join(' ');
  return (
    <div
      onClick={onClick}
      style={{
        display: 'grid',
        gridTemplateColumns: gridCols,
        gap: 'var(--sp-3)',
        alignItems: 'center',
        padding: 'var(--sp-3) var(--sp-4)',
        borderBottom: '1px solid var(--border)',
        cursor: clickable ? 'pointer' : 'default',
        transition: 'background 100ms ease',
      }}
      onMouseEnter={(e) => {
        if (clickable) {
          (e.currentTarget as HTMLDivElement).style.background =
            'var(--bg-hover, rgba(0,0,0,0.02))';
        }
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLDivElement).style.background = 'transparent';
      }}
    >
      {children}
    </div>
  );
}

// ─── Row action buttons (edit / delete) ───

export const inventoryActionBtnStyle: CSSProperties = {
  width: 32,
  height: 32,
  borderRadius: 'var(--radius-md)',
  background: 'transparent',
  border: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: 'var(--text-muted)',
  cursor: 'pointer',
  padding: 0,
};

export function InventoryRowActions({
  onEdit,
  onDelete,
}: {
  onEdit?: () => void;
  onDelete?: () => void;
}) {
  return (
    <div style={{ display: 'flex', gap: 'var(--sp-1)', justifyContent: 'flex-end' }}>
      {onEdit && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onEdit();
          }}
          aria-label="Edit"
          style={inventoryActionBtnStyle}
        >
          <NavIcon name="edit" />
        </button>
      )}
      {onDelete && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
          aria-label="Delete"
          style={inventoryActionBtnStyle}
        >
          <NavIcon name="trash" />
        </button>
      )}
    </div>
  );
}

// ─── Primary action button (uses shared @helios/ui Button) ───

export function InventoryPrimaryAction({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <Button variant="gradient" size="md" onClick={onClick}>
      + {label}
    </Button>
  );
}
